import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDisabled, useTextLanguage } from '../../../hooks/useShared';
import { useSelect } from '../../../hooks/useSelect';
import { useEdit } from '../../../hooks/useEdit';
import { Col } from 'react-bootstrap';
import { Error } from '../../shared/simple/Error';
import { Label } from '../../shared/simple/Label';
import { OptionItem } from '../../shared/simple/OptionItem';
import { TooltipIcon } from '../../shared/simple/TooltipIcon';
import { hiddenField, sOption } from '../../../constants/form';
import { StyledGroup } from '../../shared/Input/styled';
import { StyledSelect } from '../styled';
import { useSwitchDesign } from '../../../hooks/useSwitchDesign';
import { Required } from '../../shared/Input/styled';

/**
 * Component select
 * @component
 * @param {Object} field
 * @returns
 */
export const Select = ({ field, entryProps, isNewDesign=false }) => {
  const uniqueName = `${field.unique_name}__id__${field.id}`;
  const { description, languageText } = useTextLanguage();
  const { register, formState: { errors } } = useFormContext();
  const { t } = useTranslation();
  const { defaultValue, idValue, options, handleChange} = useSelect(field, uniqueName, entryProps);
  const { isDisabled } = useDisabled(field);
  const { colSize } = useEdit(field);
  const { altErrorStyle } = useSwitchDesign()

  return (
    <>
      {field && (
        <>
          {(colSize?.includes('col-before')) && (
            <Col xs={12} key={`col_before_${field.id}`}></Col>
          )}
          <Col
            className={ colSize }
            data-module={ `module-${field?.category?.field_category}` }
            data-name={ uniqueName }
            id={ `group_field_${field.id}` }
            key={ field.id }
            xs={12}
          >
            <StyledGroup className="mb-3">
              <Label htmlFor={ uniqueName } isNewDesign={isNewDesign}>
                { field[description] }{(field.required) && (
                  <Required className="text-danger">&nbsp;*</Required>)}
                {(field?.[`tooltip${languageText}`]) && (
                  <TooltipIcon 
                    tooltipText={ field[`tooltip${languageText}`] } 
                    color={isNewDesign ? 'third' : undefined}
                  />)}
              </Label>
              <StyledSelect
                { ...register(uniqueName) }
                $isNewDesign={isNewDesign}
                data-action={ field.action }
                data-category={ `category-${field?.category?.field_category }`}
                data-condition={ field.parent_condition }
                data-parent={ field.parent }
                data-id-value={ idValue }
                data-unique-name={ field.unique_name }
                disabled={ isDisabled }
                id={ field.id }
                name={ uniqueName }
                onChange={ handleChange }
              >
                <option
                  value={sOption}
                  data-code={sOption}
                >
                    { t("common.optionDefault") }
                </option>
                <option
                  className="d-none"
                  id={ hiddenField }
                  value={ hiddenField }
                >
                </option>
                <OptionItem
                  defaultValue={ defaultValue }
                  languageText={ languageText }
                  options={ options }
                  type="tier"
                  field={field}
                />
              </StyledSelect>
              {errors?.[uniqueName]?.message && (
                <Error 
                  message={ errors[uniqueName].message } 
                  altErrorMessageStyle={altErrorStyle}
                />
              )}
            </StyledGroup>
          </Col>
        </>
      )}
    </>
  );
};

Select.propTypes = {
  field: PropTypes.object.isRequired
};